import { useState, useCallback, useEffect, useMemo, useRef, Suspense } from 'react';
import type { AnnotationTextSize, AnnotationTool } from '@annoteer/annotation-engine';
import type { DocumentInfo } from '../types/document.js';
import { useSync } from '../contexts/SyncContext.js';
import { usePlatform } from '../contexts/PlatformContext.js';
import {
  clearPresentedDocument,
  presentedDocumentBefore,
  recordPresentedDocument,
  resumeOfferTrace,
  sessionMemory,
  shouldOfferResume,
} from '../utils/presenting-memory.js';
import { useMeetingState } from '../contexts/LiveShareContext.js';
import { useUndoState } from '../hooks/useUndoState.js';
import { usePresence } from '../hooks/usePresence.js';
import { useFollowDocument } from '../hooks/useFollowDocument.js';
import { useFitZoom } from '../hooks/useFitZoom.js';
import { useDocFromUrl } from '../hooks/useDocFromUrl.js';
import { useBillingRefreshOnDocument } from '../hooks/useBillingRefreshOnDocument.js';
import {
  resolveFollowPage,
  resolvePresenterView,
  shouldReturnToWhiteboard,
} from '../utils/presenter-view.js';
import { useAnnotatedExport } from '../hooks/useAnnotatedExport.js';
import { useAnnotatedImageExport } from '../hooks/useAnnotatedImageExport.js';
import type { ImageExportSources } from '../hooks/useAnnotatedImageExport.js';
import { PdfViewer, ImageViewer, DocxEditor, SpreadsheetEditor } from './lazy-viewers.js';
import { AnnotationToolbar } from './AnnotationToolbar.js';
import { DocumentUpload } from './DocumentUpload.js';
import { ExtractionPanel } from './ExtractionPanel.js';
import { MeetingControls } from './MeetingControls.js';
import { UserAvatarStack } from './UserAvatarStack.js';
import { ConnectionStatus } from './ConnectionStatus.js';
import { ZoomControls } from './ZoomControls.js';
import { BrandMark } from './BrandMark.js';
import { ManageSubscription } from './ManageSubscription.js';
import { DebugPanel } from './DebugPanel.js';
import { PickerInterruptedBanner } from './PickerInterruptedBanner.js';
import { BUILD_TAG } from '../build-tag.js';
import { formatConnectionRow } from '../utils/connection-row.js';
import { FILE_INPUT_ACCEPT, IS_COARSE_POINTER } from '../types/document.js';
import { useAuthFetch } from '../hooks/useAuthFetch.js';
import { touchProbeSummary } from '../utils/touch-probe.js';
import { WHITEBOARD_DOCUMENT_ID, isWhiteboard, whiteboardDocument } from '../utils/whiteboard.js';

/**
 * localStorage, NOT sessionStorage.
 *
 * sessionStorage is wiped when the WebView context itself is destroyed, which
 * is precisely the event these counters exist to detect -- so they reset in
 * lockstep with the thing they were measuring and reported "mounts: 1,
 * lastUpload: none" through every occurrence. A perfectly self-concealing
 * instrument, and it cost two rounds on 2026-08-31.
 *
 * localStorage survives that, so the counter only ever climbs. It is not reset
 * per meeting and is not meant to be: what matters is whether it moves across
 * one file-picker interaction.
 */
/**
 * Counts mounts across a WebView reload, which plain state cannot do. If this
 * climbs while the user only picked a file, the panel is being torn down and
 * rebuilt -- and every in-memory guard went with it.
 */
function countMount(): number {
  try {
    const n = Number(window.localStorage.getItem('annoteer.panelMounts') ?? '0') + 1;
    window.localStorage.setItem('annoteer.panelMounts', String(n));
    return n;
  } catch {
    return -1;
  }
}

function renderScale(): string {
  try {
    return window.localStorage.getItem('annoteer.renderScale') ?? 'none';
  } catch {
    return 'n/a';
  }
}

function lastExport(): string {
  try {
    return window.localStorage.getItem('annoteer.lastExport') ?? 'none';
  } catch {
    return 'n/a';
  }
}

function lastUpload(): string {
  try {
    return window.localStorage.getItem('annoteer.lastUpload') ?? 'none';
  } catch {
    return 'n/a';
  }
}

export function SidePanel() {
  const { connectionState, undoManager } = useSync();
  const { status: authStatus } = useAuthFetch();
  const { adapter, user, context } = usePlatform();
  const { canUndo, canRedo } = useUndoState(undoManager);
  // Both: `remoteUsers` is connections (the stage relay below has to find our
  // own stage client), `viewers` is people (what the avatar row should show).
  const { remoteUsers, viewers } = usePresence();
  const {
    isPresenting,
    isFollowing,
    isSuspended,
    followModeState,
    updateViewState,
    endSuspension,
    eventCount,
  } = useMeetingState();
  const [activeTool, setActiveTool] = useState<AnnotationTool>('select');
  const [activeColor, setActiveColor] = useState('#dc2626');
  const [activeStrokeWidth, setActiveStrokeWidth] = useState(2);
  const [activeTextSize, setActiveTextSize] = useState<AnnotationTextSize>('medium');
  const [documentInfo, setDocumentInfo] = useState<DocumentInfo | null>(null);
  useBillingRefreshOnDocument(documentInfo?.documentId);
  // A phone cannot open a document, so with nothing open it gets the meeting's
  // whiteboard instead of a waiting message (certification issue #14). A
  // desktop keeps the upload prompt: here it is the primary action. Rendered
  // only, never stored in documentInfo -- see whiteboard.ts.
  const shownDocument = documentInfo ?? (IS_COARSE_POINTER ? whiteboardDocument() : null);
  const [showExtraction, setShowExtraction] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const {
    zoom: zoomLevel,
    fitZoom,
    fitMode,
    reportFitZoom,
    chooseZoom,
    chooseFit,
    resetZoom,
  } = useFitZoom();
  const {
    canExport: canExportAnnotated,
    isExporting,
    exportError,
    exportedName,
    exportAnnotated,
  } = useAnnotatedExport(documentInfo);
  // Images have no annotated export of their own -- ImageViewer hands us a
  // getter for the <img> and the Fabric layer, and we composite them here so
  // the Download button sits in the same place it does for every other format.
  const imageExportRef = useRef<(() => ImageExportSources | null) | null>(null);
  const [canExportImage, setCanExportImage] = useState(false);
  const handleImageExportReady = useCallback(
    (getSources: (() => ImageExportSources | null) | null) => {
      imageExportRef.current = getSources;
      setCanExportImage(getSources != null);
    },
    [],
  );
  const {
    isExporting: isExportingImage,
    exportError: imageExportError,
    exportedName: imageExportedName,
    exportAnnotatedImage,
  } = useAnnotatedImageExport(shownDocument?.fileName);
  const handleExportImage = useCallback(() => {
    const sources = imageExportRef.current?.();
    if (sources) void exportAnnotatedImage(sources);
  }, [exportAnnotatedImage]);
  const isFollowingRef = useRef(isFollowing);
  isFollowingRef.current = isFollowing;
  const isSuspendedRef = useRef(isSuspended);
  isSuspendedRef.current = isSuspended;
  // Read in callbacks, so it is a ref rather than a dependency: making it one
  // would rebuild handleDocumentLoaded every time the presenter turns a page.
  const presenterDocIdRef = useRef<string | null>(null);
  presenterDocIdRef.current = resolvePresenterView(followModeState)?.documentId ?? null;
  /**
   * The user is deliberately looking at something other than the presenter's
   * document — they opened their own, or closed the presented one.
   *
   * This is LOCAL state on purpose. The obvious tool is Live Share's own
   * suspension, and it has now been tried twice and failed the same way both
   * times (ff654b3, and again on 2026-08-27): the phone ends up suspended,
   * endSuspension does not bring it back, and because a suspended client stops
   * receiving the presenter's view there is nothing left to recover with. It is
   * a state we cannot inspect and cannot reliably leave.
   *
   * Staying in follow mode and simply declining to apply the presenter's
   * document keeps every Live Share update flowing, so the way back is always
   * available: clear this flag and the next update applies.
   */
  const [followOverride, setFollowOverride] = useState(false);
  /** Bumped by Rejoin to re-run the apply effect when the document is unchanged. */
  const [followEpoch, setFollowEpoch] = useState(0);
  /**
   * The user has taken over from the presenter's view in THIS mount, so the
   * ?docId path below must not reach in and replace what they chose.
   */
  const userOpenedRef = useRef(false);
  const mountCountRef = useRef<number>(0);
  if (mountCountRef.current === 0) mountCountRef.current = countMount();

  // Five taps on the wordmark reveal the state readout. Deliberately obscure:
  // it must never appear in a certification screenshot, and the phone has no
  // way to pass a query parameter.
  const [showDebug, setShowDebug] = useState(false);
  // A delete control has to know when something is selected, and needs a way
  // to act on it. Until 2026-09-07 deleting was Delete/Backspace only, which a
  // phone has no way to press.
  const [hasSelection, setHasSelection] = useState(false);
  const deleteSelectedRef = useRef<(() => void) | null>(null);
  /**
   * Live page geometry for the readout. A ref, not state: the viewer owns the
   * Fabric canvas, and this must not re-render anything when it is populated.
   */
  const geometryReadoutRef = useRef<(() => Record<string, string>) | null>(null);
  // Selecting a text or note shows its size in the picker, so tapping another
  // size always changes it -- otherwise a size could only be chosen before
  // writing (reported from a phone 2026-09-13).
  const handleSelectionChange = useCallback((has: boolean, textSize: AnnotationTextSize | null) => {
    setHasSelection(has);
    if (textSize) setActiveTextSize(textSize);
  }, []);
  /**
   * Set once this panel has put a document on the stage. Loading another
   * document afterwards re-shares, so the stage follows instead of keeping the
   * old one until Share is pressed again (reported from a phone 2026-09-13).
   * Only the panel that shared does this: any other participant's panel
   * opening a document must not take over the stage.
   */
  const sharedToStageRef = useRef(false);
  const adapterRef = useRef(adapter);
  adapterRef.current = adapter;
  const tapsRef = useRef<number[]>([]);
  const handleBrandTap = useCallback(() => {
    const now = Date.now();
    tapsRef.current = [...tapsRef.current, now].filter((t) => now - t < 3000);
    if (tapsRef.current.length >= 5) {
      tapsRef.current = [];
      setShowDebug((v) => !v);
    }
  }, []);

  /**
   * Move the stage to `documentId`, or to the whiteboard when null, if this
   * panel put something on the stage. The stage reads its document from the
   * URL it was shared with, and nothing else reaches it (follow-me does not --
   * verified 2026-08-21), so re-sharing is the only way to move it. Asked
   * first, because the user may have stopped sharing in Teams since.
   */
  const reshareToStage = useCallback((documentId: string | null) => {
    if (!sharedToStageRef.current) return;
    void (async () => {
      const platform = adapterRef.current;
      if (!(await platform.isAppSharingToStage())) {
        sharedToStageRef.current = false;
        return;
      }
      try {
        const url = new URL(window.location.origin);
        if (documentId) url.searchParams.set('docId', documentId);
        await platform.shareToStage(url.toString());
      } catch (err) {
        console.error('Re-sharing to the stage failed:', err);
      }
    })();
  }, []);

  /**
   * The readout is computed during render, but the geometry it reports changes
   * when someone draws, zooms or turns a page -- none of which re-render this
   * component. Ticking only while the panel is open keeps it honest without
   * costing anything the rest of the time.
   */
  const [, setDebugTick] = useState(0);
  useEffect(() => {
    if (!showDebug) return;
    const id = window.setInterval(() => setDebugTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [showDebug]);

  const handleDocumentLoaded = useCallback(
    (doc: DocumentInfo) => {
      setDocumentInfo(doc);
      resetZoom();
      // Opening a document yourself while following is a deliberate choice to
      // look at something other than the presenter's view. Without this the
      // upload is thrown away: the follow hook sees a document id that differs
      // from the presenter's, re-fetches theirs, and puts it back on top.
      // Reported from a real phone on 2026-08-27 — "I grab a PDF and Machine A's
      // document comes back".
      // Unconditional, and deliberately NOT gated on isFollowing. Opening a
      // document by hand is an explicit statement of intent; whether Live Share
      // reports "following" in this exact millisecond is a different question,
      // asked at the wrong moment. The overwrite lands LATER and asynchronously,
      // so a follow state that reads false now and true a second afterwards --
      // the Android file picker backgrounds the WebView, and Live Share already
      // alternates between the presenter's value and our own echo -- walked
      // straight past the old `if (isFollowingRef.current)` gate. Reported still
      // broken from a real phone on 2026-08-30 against exactly that gate.
      if (presenterDocIdRef.current) {
        appliedFollowDocIdRef.current = presenterDocIdRef.current;
      }
      setFollowOverride(true);
      userOpenedRef.current = true;
      // Stamp the document ID into the URL so a second window (and the meeting
      // stage) can open the same document by URL.
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('docId', doc.documentId);
        window.history.replaceState({}, '', url.toString());
      } catch {
        // ignore URL update failures
      }
      reshareToStage(doc.documentId);
    },
    [reshareToStage, resetZoom],
  );

  const handleCloseDocument = useCallback(() => {
    setDocumentInfo(null);
    setShowExtraction(false);
    setActiveTool('select');
    // On a phone the whiteboard comes back in this document's place, and must
    // not inherit a zoom that was fitted to a different page.
    resetZoom();
    // Closing while following is the same deliberate choice as opening your own
    // document: remember the presenter's document as handled, so it does not
    // come straight back a second later. Rejoin is the way back, and it stays
    // visible for exactly as long as this holds.
    if (isFollowingRef.current) {
      // Only when we actually know it -- overwriting with null would forget
      // the document we are already showing, which is the presenter's.
      if (presenterDocIdRef.current) {
        appliedFollowDocIdRef.current = presenterDocIdRef.current;
      }
      setFollowOverride(true);
    } else {
      appliedFollowDocIdRef.current = null;
    }
    userOpenedRef.current = true;
    // Strip ?docId from the URL so a refresh doesn't auto-reload (and so the
    // URL hook does not think there is one to load).
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('docId');
      window.history.replaceState({}, '', url.toString());
    } catch {
      // ignore URL update failures
    }
    // A stage we shared would otherwise keep the closed document.
    reshareToStage(null);
  }, [reshareToStage, resetZoom]);

  /**
   * Return to the presenter's view. Clearing the applied id is what makes it
   * do anything: both Close and a local upload mark the presenter's document
   * as handled, so without this the follow effect would skip it.
   */
  const handleRejoinPresenter = useCallback(() => {
    appliedFollowDocIdRef.current = null;
    setFollowOverride(false);
    userOpenedRef.current = false;
    // The presenter's document is usually already fetched and unchanged, so the
    // apply effect would not re-run on its own.
    setFollowEpoch((e) => e + 1);
    // Only relevant if MeetingControls' own "Navigate freely" put us here.
    if (isSuspendedRef.current) void endSuspension();
  }, [endSuspension]);

  // Auto-load the document named by ?docId=<id> (localhost two-window testing;
  // the meeting stage uses the same hook).
  const { urlDocument } = useDocFromUrl(documentInfo?.documentId);
  const urlDocAppliedRef = useRef(false);
  useEffect(() => {
    // Never over the top of a document the user opened here themselves.
    if (userOpenedRef.current) return;
    if (urlDocument && !urlDocAppliedRef.current) {
      urlDocAppliedRef.current = true;
      // On THIS surface a ?docId can only have come from handleDocumentLoaded,
      // i.e. from the user's own pick -- nothing else ever writes it. So
      // finding one after a remount means restoring their choice, not the
      // presenter's, and the follow effect has to be told to stand down or it
      // races the restore and wins about half the time.
      userOpenedRef.current = true;
      appliedFollowDocIdRef.current = presenterDocIdRef.current;
      setFollowOverride(true);
      setDocumentInfo(urlDocument);
    }
  }, [urlDocument]);

  // When presenting, broadcast view state on document change. Read the id out
  // first so the effect body depends on the same narrow value the dep array
  // does — depending on the whole `documentInfo` object would re-broadcast on
  // every unrelated field change.
  const presentedDocumentId = documentInfo?.documentId;

  // Whether this panel LOST a document to a rebuild, as opposed to never having
  // had one. Reproduced on two devices 2026-09-19: Machine A presenting a PDF
  // with the phone following, panel closed and reopened, and the phone jumped
  // to the whiteboard. Live Share hands the presenter role back, the rebuilt
  // panel has no documentInfo, and the effect below then announces the
  // whiteboard -- which followers correctly read as "the presenter closed it".
  //
  // Same discriminator as the Resume offer, and for the same reason: watch the
  // transition, not the state. A page that is alive to see a document go away
  // is a real close; a page that simply never had one may just be new.
  const presentingStorage = useMemo(() => sessionMemory(), []);
  const lostDocumentRef = useRef(false);
  const lostDocumentInitRef = useRef(false);
  useEffect(() => {
    if (lostDocumentInitRef.current || !context?.meetingId) return;
    lostDocumentInitRef.current = true;
    lostDocumentRef.current =
      presentedDocumentBefore(presentingStorage, 'side-panel', context.meetingId) != null;
  }, [presentingStorage, context?.meetingId]);

  // When we have shared to the stage, the presenter navigates THERE, not here,
  // so broadcasting our own page pins followers to whatever page this panel
  // happens to be on -- page 1, while the presenter reads page 6. Observed on a
  // phone 2026-08-27: the presence dot tracked the presenter (Yjs awareness)
  // while the page did not (Live Share).
  //
  // The stage cannot broadcast for itself: two writers is the flicker 733640f
  // fixed, and restoring it reproduced that within minutes. So it reports its
  // page over awareness and this panel relays it -- one writer still, so
  // flicker is impossible rather than merely avoided.
  //
  // A meeting has exactly one stage, and awareness is scoped to the document's
  // room, so a stage seen here is showing this document.
  // OUR OWN stage, matched on userId -- not merely "a stage".
  //
  // Any awareness client reporting surface 'stage' used to win here, and stage
  // clients linger until awareness times them out. So a phone that had been
  // showing the stage earlier in the meeting, sitting on page 1, pinned the
  // presenter's broadcast to page 1 however far they navigated: the presence
  // dot moved (Yjs awareness) and the page did not (Live Share). Reported from
  // a phone 2026-08-31, and introduced by the relay itself.
  //
  // The intent was only ever "we shared to the stage, so the presenter is
  // navigating there rather than here". That is our own stage and nobody
  // else's, which userId now says exactly.
  const localUserId = user?.id;
  const stagePage = remoteUsers.find(
    (u) => u.surface === 'stage' && localUserId != null && u.userId === localUserId,
  )?.currentPage;
  // Latch the last stage page we saw, and never fall back to this panel's page
  // on a momentary gap.
  //
  // `stagePage ?? currentPage` alternated: awareness entries blink, so when the
  // stage's entry was briefly absent the broadcast dropped to THIS panel's page
  // -- page 1, while the presenter read page 7 on the stage -- and jumped back
  // on the next update. Followers flipped between the two every few seconds.
  // Reported from a phone 2026-08-31, and the third time this codebase has
  // produced a flicker by letting two page values compete.
  //
  // Worst case now is that followers hold the stage's last known page, which is
  // stale rather than oscillating. Stale is recoverable; a page that moves under
  // the reader every three seconds is unusable.
  const lastStagePageRef = useRef<number | undefined>(undefined);
  if (stagePage != null) lastStagePageRef.current = stagePage;
  const broadcastPage = stagePage ?? lastStagePageRef.current ?? currentPage;
  // With no document open the presenter is on the whiteboard, and says so.
  // Broadcasting nothing left followers on a document the presenter had
  // closed (reported from a phone 2026-09-14). The id, never null: a null
  // documentId is how resolvePresenterView recognises a placeholder.
  useEffect(() => {
    if (!isPresenting) return;
    if (presentedDocumentId) {
      recordPresentedDocument(
        presentingStorage,
        'side-panel',
        context?.meetingId,
        presentedDocumentId,
      );
      lostDocumentRef.current = false;
      void updateViewState({ documentId: presentedDocumentId, currentPage: broadcastPage });
      return;
    }
    // No document, and this page never watched one go away -- it was rebuilt
    // under a presenter. Say nothing: followers keep what they are reading.
    // Broadcasting here is what yanked the phone to the whiteboard.
    if (lostDocumentRef.current) return;
    clearPresentedDocument(presentingStorage, 'side-panel');
    void updateViewState({ documentId: WHITEBOARD_DOCUMENT_ID, currentPage: 1 });
  }, [
    isPresenting,
    presentedDocumentId,
    broadcastPage,
    updateViewState,
    presentingStorage,
    context?.meetingId,
  ]);

  // Track page changes from PdfViewer
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  // Put the document on the meeting stage. The id travels in the URL because
  // the stage boots as a separate instance of this app with no state, and
  // Live Share follow-me does not reach it (verified in a real meeting
  // 2026-08-21). Teams' own Share button opens the bare manifest URL, so it
  // still lands on the upload prompt — this is the one that carries context.
  const [isSharingToStage, setIsSharingToStage] = useState(false);
  const shareDocumentId = documentInfo?.documentId;
  const handleShareToStage = useCallback(async () => {
    if (!shareDocumentId) return;
    setIsSharingToStage(true);
    try {
      const url = new URL(window.location.origin);
      url.searchParams.set('docId', shareDocumentId);
      await adapter.shareToStage(url.toString());
      sharedToStageRef.current = true;
    } catch (err) {
      console.error('Share to stage failed:', err);
    } finally {
      setIsSharingToStage(false);
    }
  }, [adapter, shareDocumentId]);

  // Follow presenter's document when following
  const { followDocument, isLoadingFollow } = useFollowDocument(
    followModeState,
    isFollowing,
    documentInfo?.documentId,
  );
  // Guard on the id, not a one-shot ref like the URL path below: the presenter
  // may legitimately switch documents many times, but re-applying one we are
  // ALREADY showing would remount the viewer and throw away zoom and fit state
  // for no reason.
  const appliedFollowDocIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!followDocument) return;
    // The user is deliberately looking at their own document. This is the
    // guard that actually holds: the remembered id below is a second line of
    // defence, and cannot be relied on alone because Live Share alternates
    // between the presenter's value and our own echo, so the presenter's id is
    // sometimes unknown at the exact moment the user opens something.
    if (followOverride) return;
    // The last document fetched stays in followDocument after the presenter
    // closes it; re-applying it would undo the move to the whiteboard.
    if (isWhiteboard(presenterDocIdRef.current)) return;
    if (appliedFollowDocIdRef.current === followDocument.documentId) return;
    appliedFollowDocIdRef.current = followDocument.documentId;
    // Applying the presenter's document IS rejoining them, however we got here
    // -- via Rejoin, or because they moved on to a different document while we
    // were looking at our own. Either way we are back on their view.
    setFollowOverride(false);
    setDocumentInfo(followDocument);
    // followEpoch is a dependency so Rejoin can re-apply a document that has
    // not changed; it is deliberately unused in the body.
  }, [followDocument, followEpoch, followOverride]);

  // The presenter closed their document and is on the whiteboard: follow them
  // there. See shouldReturnToWhiteboard.
  const presenterViewDocId = resolvePresenterView(followModeState)?.documentId ?? null;
  const openDocumentId = documentInfo?.documentId;
  useEffect(() => {
    if (
      !shouldReturnToWhiteboard(presenterViewDocId, openDocumentId, isFollowing, followOverride)
    ) {
      return;
    }
    appliedFollowDocIdRef.current = WHITEBOARD_DOCUMENT_ID;
    setDocumentInfo(null);
    setShowExtraction(false);
    setActiveTool('select');
    resetZoom();
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('docId');
      window.history.replaceState({}, '', url.toString());
    } catch {
      // ignore URL update failures
    }
  }, [presenterViewDocId, openDocumentId, isFollowing, followOverride, resetZoom]);

  // Follow the presenter's page — never our own echoed back. See
  // resolveFollowPage for why isLocalValue has to be honoured.
  const followPage = resolveFollowPage(
    followModeState,
    isFollowing,
    isSuspended,
    documentInfo?.documentId,
  );

  // Every viewer is keyed by document. Without a key, opening one image after
  // another -- the whiteboard is an image -- kept the same viewer and its Fabric
  // canvas, and the previous document's annotations stayed drawn over the new
  // one (reported from Machine A 2026-09-13).
  const renderViewer = (doc: DocumentInfo) => {
    if (doc.documentType === 'pdf' || doc.documentType === 'presentation') {
      return (
        <PdfViewer
          key={doc.documentId}
          pdfData={doc.data}
          documentId={doc.documentId}
          activeTool={activeTool}
          activeColor={activeColor}
          activeStrokeWidth={activeStrokeWidth}
          activeTextSize={activeTextSize}
          onToolChange={setActiveTool}
          followPage={followPage}
          isFollowing={isFollowing && !followOverride}
          onPageChange={handlePageChange}
          zoomOverride={zoomLevel}
          onFitZoomCalculated={reportFitZoom}
          onSelectionChange={handleSelectionChange}
          deleteSelectedRef={deleteSelectedRef}
          geometryReadoutRef={geometryReadoutRef}
        />
      );
    }

    if (doc.documentType === 'document') {
      return (
        <DocxEditor
          key={doc.documentId}
          docxData={doc.data}
          fileName={doc.fileName}
          documentId={doc.documentId}
          zoomOverride={zoomLevel}
        />
      );
    }

    if (doc.documentType === 'spreadsheet') {
      return (
        <SpreadsheetEditor
          key={doc.documentId}
          spreadsheetData={doc.data}
          fileName={doc.fileName}
          documentId={doc.documentId}
          zoomOverride={zoomLevel}
        />
      );
    }

    return (
      <ImageViewer
        key={doc.documentId}
        imageData={doc.data}
        mimeType={doc.mimeType}
        documentId={doc.documentId}
        activeTool={activeTool}
        activeColor={activeColor}
        activeStrokeWidth={activeStrokeWidth}
        activeTextSize={activeTextSize}
        onToolChange={setActiveTool}
        zoomOverride={zoomLevel}
        onFitZoomCalculated={reportFitZoom}
        onExportReady={handleImageExportReady}
        onSelectionChange={handleSelectionChange}
        deleteSelectedRef={deleteSelectedRef}
        geometryReadoutRef={geometryReadoutRef}
      />
    );
  };

  return (
    <div className="side-panel">
      <div className="side-panel__header">
        <span className="side-panel__brand" onClick={handleBrandTap}>
          <BrandMark size={20} />
          <span className="side-panel__title">Annoteer</span>
        </span>
        {documentInfo && (
          <button
            className="side-panel__close-doc"
            onClick={handleCloseDocument}
            title="Close document"
          >
            &times; Close
          </button>
        )}
        <UserAvatarStack users={viewers} />
        <ManageSubscription />
        <ConnectionStatus state={connectionState} />
      </div>

      {(exportedName ?? imageExportedName) && !exportError && !imageExportError && (
        <div className="export-done" role="status">
          Saved <strong>{exportedName ?? imageExportedName}</strong>. If it did not appear, your
          Teams client may be blocking downloads — try Teams on Windows, macOS or the web.
        </div>
      )}

      {(exportError ?? imageExportError) && (
        <div className="export-error" role="alert">
          Download failed: {exportError ?? imageExportError}
        </div>
      )}

      <PickerInterruptedBanner />

      {showDebug && (
        <DebugPanel
          onClose={() => setShowDebug(false)}
          values={{
            build: BUILD_TAG,
            surface: 'side-panel',
            mounts: mountCountRef.current,
            userOpened: userOpenedRef.current,
            lastUpload: lastUpload(),
            lastExport: lastExport(),
            renderScale: renderScale(),
            urlDoc: new URLSearchParams(window.location.search).get('docId')?.slice(0, 8) ?? null,
            authed: authStatus,
            picker: FILE_INPUT_ACCEPT ? 'filtered' : 'any',
            presenting: isPresenting,
            // After a rebuild this says whether the panel still remembers it
            // was presenting -- i.e. whether the Resume offer should be on
            // screen. "presenting false, wasPresenting true" is the lapse
            // this whole feature exists for.
            wasPresenting: shouldOfferResume(
              sessionMemory(),
              'side-panel',
              context?.meetingId ?? null,
            ),
            // Whether the Resume strip was ever armed, and whether it ever
            // reached the screen. "armed X, shown X" means it appeared. "armed X,
            // NOT shown" is the bug ONLY when presenting is false; with
            // presenting true, Live Share handed the role back first and there
            // was nothing to resume. CUMULATIVE like mounts -- check the
            // timestamps against the clock.
            resumeOffer: resumeOfferTrace(),
            // true = this panel was presenting a document before it was
            // rebuilt, so it must NOT announce the whiteboard. Cleared the
            // moment a document is open again.
            lostDoc: lostDocumentRef.current,
            following: isFollowing,
            lsEvents: eventCount,
            lsType: followModeState?.type ?? null,
            suspended: isSuspended,
            override: followOverride,
            loadingFollow: isLoadingFollow,
            followType: followModeState?.type ?? null,
            isLocalValue: followModeState?.isLocalValue ?? null,
            presenterDoc: presenterDocIdRef.current?.slice(0, 8) ?? null,
            presenterPage: resolvePresenterView(followModeState)?.currentPage ?? null,
            appliedDoc: appliedFollowDocIdRef.current?.slice(0, 8) ?? null,
            openDoc: documentInfo?.documentId.slice(0, 8) ?? null,
            openType: documentInfo?.documentType ?? null,
            followPage: followPage ?? null,
            stagePage: stagePage ?? null,
            lastStagePage: lastStagePageRef.current ?? null,
            broadcastPage,
            page: currentPage,
            conn: formatConnectionRow(connectionState, shownDocument != null),
            touch: touchProbeSummary(),
            // Page-relative geometry of every mark on the current page, read
            // from Fabric rather than Yjs. The two clients store identical
            // data in all three coordinate bugs found so far, so only the
            // rendered geometry can disagree -- and a fraction of the page is
            // scale-invariant, so the SAME numbers must appear on a phone and
            // on a desktop. Compare row g:<id> against g:<id>, and watch the
            // w/h as hard as the x/y: a shape can sit in exactly the right
            // place while being a third too small.
            ...(geometryReadoutRef.current?.() ?? {}),
          }}
        />
      )}

      <MeetingControls surface="side-panel" />

      {!shownDocument ? (
        <>
          {/* Fetching the presenter's document takes a second or two, and
              showing the upload prompt meanwhile dangles a file picker that is
              about to be torn out from under the user — they tap "Choose a
              file", the document lands, the prompt unmounts, and the tap
              appears to do nothing. Reported from a phone on 2026-08-27. The
              hook has always exposed this flag; nothing had ever read it. */}
          {isLoadingFollow && !followOverride ? (
            <p className="side-panel__follow-loading">Opening the presenter's document…</p>
          ) : (
            <DocumentUpload onDocumentLoaded={handleDocumentLoaded} />
          )}
          {/* With no document there is no zoom row, so this is the only copy of
              Rejoin outside MeetingControls — which Teams can cover on Android.
              Closing while following suspends, so without it the user could be
              left with no way back to the presenter at all. */}
          {(followOverride || isSuspended) && (
            <button className="side-panel__rejoin" onClick={handleRejoinPresenter}>
              Rejoin the presenter
            </button>
          )}
        </>
      ) : (
        <>
          {shownDocument.documentType !== 'document' &&
            shownDocument.documentType !== 'spreadsheet' && (
              <AnnotationToolbar
                activeTool={activeTool}
                activeColor={activeColor}
                activeStrokeWidth={activeStrokeWidth}
                onToolChange={setActiveTool}
                onColorChange={setActiveColor}
                onStrokeWidthChange={setActiveStrokeWidth}
                activeTextSize={activeTextSize}
                onTextSizeChange={setActiveTextSize}
              />
            )}

          <ZoomControls
            onCloseDocument={documentInfo ? handleCloseDocument : undefined}
            zoom={zoomLevel}
            onZoomChange={chooseZoom}
            fitZoom={fitZoom}
            onFitSelect={chooseFit}
            fitMode={fitMode}
            canUndo={canUndo}
            canRedo={canRedo}
            onUndo={() => undoManager?.undo()}
            onRedo={() => undoManager?.redo()}
            canDelete={hasSelection}
            onDeleteSelected={() => deleteSelectedRef.current?.()}
            onExtractClick={
              shownDocument.documentType === 'pdf' || shownDocument.documentType === 'presentation'
                ? () => setShowExtraction(true)
                : undefined
            }
            onExportClick={
              // Teams for Android refuses the download itself: tapping this
              // produced Teams' own dialog, "Cette action n'est actuellement
              // pas prise en charge sur mobile" -- not our error, and not
              // something the app can work around. Its wording ("a problem
              // occurred") reads as a bug in Annoteer, which is worse than
              // having no button. A participant on desktop can download the
              // same annotated copy; the annotations are shared.
              IS_COARSE_POINTER
                ? undefined
                : canExportAnnotated
                  ? () => void exportAnnotated()
                  : canExportImage
                    ? handleExportImage
                    : undefined
            }
            isExporting={isExporting || isExportingImage}
            onRejoinPresenter={followOverride || isSuspended ? handleRejoinPresenter : undefined}
            openDocumentSlot={
              <DocumentUpload variant="inline" onDocumentLoaded={handleDocumentLoaded} />
            }
            // The whiteboard has no id the stage could fetch.
            onShareToStage={documentInfo ? () => void handleShareToStage() : undefined}
            isSharingToStage={isSharingToStage}
          />

          {!documentInfo && (
            <p className="whiteboard-hint" role="note">
              {isLoadingFollow && !followOverride ? (
                "Opening the presenter's document…"
              ) : (
                <>
                  <strong>Whiteboard</strong> — everyone in the meeting can draw here. A shared
                  document replaces it as soon as a presenter opens one.
                </>
              )}
            </p>
          )}

          <div className="side-panel__viewer">
            <Suspense fallback={<div className="viewer-suspense">Loading viewer…</div>}>
              {renderViewer(shownDocument)}
            </Suspense>
          </div>

          {showExtraction &&
            (shownDocument.documentType === 'pdf' ||
              shownDocument.documentType === 'presentation') && (
              <ExtractionPanel
                pdfData={shownDocument.data}
                fileName={shownDocument.fileName}
                onClose={() => setShowExtraction(false)}
              />
            )}
        </>
      )}
    </div>
  );
}
